import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    HostListener,
    inject,
    ViewChild,
    ChangeDetectionStrategy,
} from '@angular/core';
import {
    get as _get,
    isBoolean as _isBoolean,
    toNumber as _toNumber,
    isFinite as _isFinite,
    round as _round,
    toString as _toString,
    isNil as _isNil,
    every as _every,
} from 'lodash';
import { FormatNumberPipeModule } from 'src/app/shared/pipes/format-number.pipe';
import { CalculatorInputComponent } from './components/calculator-input.component';
import { PriceCalSaveService } from './price-cal.service';
import { forkJoin, Observable } from 'rxjs';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { DxLoadPanelModule } from 'devextreme-angular';
import {
    MessageType,
    NotificationService,
    UtilService,
} from 'src/app/shared/services';

@Component({
    selector: 'app-price-cal-filler',
    templateUrl: './price-cal-filler.component.html',
    styleUrls: ['./price-cal-filler.component.scss'],
    imports: [
        CommonModule,
        FormatNumberPipeModule,
        DxLoadPanelModule,
        CalculatorInputComponent,
        RouterLink,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [PriceCalSaveService],
})
export class PriceCalFillerComponent implements AfterViewInit {
    @ViewChild('pYuanPriceCtl', {}) pYuanPriceCtl!: CalculatorInputComponent;
    @ViewChild('exchangeRateCtl') exchangeRateCtl!: CalculatorInputComponent;
    @ViewChild('shippingChinaPctCtl')
    shippingChinaPctCtl!: CalculatorInputComponent;
    @ViewChild('transactionFeePctCtl')
    transactionFeePctCtl!: CalculatorInputComponent;
    @ViewChild('shippingThaiPctCtl')
    shippingThaiPctCtl!: CalculatorInputComponent;
    @ViewChild('hiddenCostPctCtl') hiddenCostPctCtl!: CalculatorInputComponent;
    @ViewChild('vatPctCtl') vatPctCtl!: CalculatorInputComponent;
    @ViewChild('expectedProfitPctCtl')
    expectedProfitPctCtl!: CalculatorInputComponent;
    @ViewChild('expectedProfitBahtCtl')
    expectedProfitBahtCtl!: CalculatorInputComponent;
    @ViewChild('platformFeePctCtl')
    platformFeePctCtl!: CalculatorInputComponent;

    // === Constant Value ===
    DECIMAL_POINT = 2;
    SCOPE = 'price-cal-filler';

    // === Default Value ===
    defYuanPrice = (5).toFixed(this.DECIMAL_POINT); // ราคาขายร้านจีน (หยวน)
    defExchangeRate = (5).toFixed(this.DECIMAL_POINT); // อัตราแลกเปลี่ยน
    defShippingChinaPct = (1).toFixed(this.DECIMAL_POINT); // ค่าส่งในจีน (%)
    // ค่าธรรมเนียมชำระเงิน (%) — TrueMoney's cut on paying 1688, charged on the
    // product plus the seller's China-domestic shipping. A payment-channel
    // rate, so it is saved globally (pid -1) like the exchange rate, not per
    // product.
    defTransactionFeePct = (2).toFixed(this.DECIMAL_POINT);
    defShippingThaiPct = (10).toFixed(this.DECIMAL_POINT); // ค่าขนส่งมาไทย (%)
    defHiddenCostPct = (1).toFixed(this.DECIMAL_POINT); // ต้นทุนแฝง (%)
    defPlatformFeePct = (22).toFixed(this.DECIMAL_POINT); // ค่าธรรมเนียม (%)
    defVatPct = (7).toFixed(this.DECIMAL_POINT); // Vat (%)
    defExpectedProfitPct = (100).toFixed(this.DECIMAL_POINT); // กำไรที่คาดหวัง (%)
    defExpectedProfitBaht = (0).toFixed(this.DECIMAL_POINT);

    // === Calcuated Value ===
    calculatedProductCostFromChina = 0;
    // Shown so the fee's basis is visible: it is a cut of the 1688 payment,
    // not of the product cost the other percentages share.
    calculatedChinaPaymentAmount = 0;
    calculatedTransactionFee = 0;
    calculatedTotalDeducted = 0;
    calculatedBahtSellingPlatformPrice = 0;
    calcuatedBahtSellingLnwShopPrice = 0;
    calculatedTotalCost = 0;

    // === Status Display Text ===
    currentDisplaySku = '-';
    statusText = '';

    // === State ===
    loading = false;
    blocking = false;
    currentPid = 1;
    messageSourceWindow: Window | null = null;
    useExpectedProfitPct = true;

    private cdref = inject(ChangeDetectorRef);
    private priceCalSaveService = inject(PriceCalSaveService);
    private notificationService = inject(NotificationService);
    private utilService = inject(UtilService);

    ngAfterViewInit(): void {
        this.initDefaults();
        this.calculate();
    }

    /**
     * Seed each input with its default value. The ViewChild controls are the
     * single source of truth for input values, so defaults are applied
     * imperatively here rather than via a `[value]` template binding — a
     * binding to a constant would silently re-clobber user/loaded values on
     * subsequent change-detection passes.
     */
    private initDefaults(): void {
        this.pYuanPriceCtl.value = this.defYuanPrice;
        this.exchangeRateCtl.value = this.defExchangeRate;
        this.shippingChinaPctCtl.value = this.defShippingChinaPct;
        this.transactionFeePctCtl.value = this.defTransactionFeePct;
        this.shippingThaiPctCtl.value = this.defShippingThaiPct;
        this.hiddenCostPctCtl.value = this.defHiddenCostPct;
        this.vatPctCtl.value = this.defVatPct;
        this.expectedProfitPctCtl.value = this.defExpectedProfitPct;
        this.expectedProfitBahtCtl.value = this.defExpectedProfitBaht;
        this.platformFeePctCtl.value = this.defPlatformFeePct;
    }

    @HostListener('window:message', ['$event'])
    onMessage(event: MessageEvent): void {
        const scope = _get(event, 'data.scope');
        const status = _get(event, 'data.status');
        const data = _get(event, 'data.data');
        if (scope === this.SCOPE && status === 'send_data' && data) {
            console.log(data);
            this.messageSourceWindow = event.source as Window;
            if (this.messageSourceWindow) {
                this.messageSourceWindow.postMessage(
                    {
                        scope: this.SCOPE,
                        status: 'received',
                    },
                    '*',
                );
            }

            this.currentDisplaySku = _get(data, 'props.code', '-');
            const pid = _toNumber(_get(data, 'id', 0));
            if (pid && _isFinite(pid)) {
                this.currentPid = pid;
                this.fetchSavedParams();
            }
        }
    }

    private startLoading(status: string, blocking = true) {
        this.loading = true;
        this.blocking = blocking;
        this.statusText = status ? status : '';
        this.cdref.detectChanges();
    }

    private endLoading(endText = '') {
        this.loading = false;
        this.blocking = false;
        this.statusText = endText;
        this.cdref.detectChanges();
    }

    fetchSavedParams() {
        if (!this.loading && this.currentPid) {
            this.startLoading(`กำลังโหลดข้อมูล ...`);
            this.priceCalSaveService
                .load(this.currentPid)
                .subscribe((results) => {
                    const pid = _get(results, 'pid');
                    if (pid === this.currentPid) {
                        const setControlValue = (
                            key: string,
                            defaultValue: string | number,
                            control: CalculatorInputComponent,
                        ) => {
                            const val: any = _get(results, key, defaultValue);
                            const numVal = _toNumber(val);
                            if (_isFinite(numVal)) {
                                control.value = this.round(numVal).toFixed(
                                    this.DECIMAL_POINT,
                                );
                            } else {
                                control.value = (1).toFixed(this.DECIMAL_POINT);
                            }
                        };

                        setControlValue(
                            'pYuanPrice',
                            this.defYuanPrice,
                            this.pYuanPriceCtl,
                        );
                        setControlValue(
                            'exchangeRate',
                            this.defExchangeRate,
                            this.exchangeRateCtl,
                        );
                        setControlValue(
                            'shippingChinaPct',
                            this.defShippingChinaPct,
                            this.shippingChinaPctCtl,
                        );
                        setControlValue(
                            'transactionFeePct',
                            this.defTransactionFeePct,
                            this.transactionFeePctCtl,
                        );
                        setControlValue(
                            'shippingThaiPct',
                            this.defShippingThaiPct,
                            this.shippingThaiPctCtl,
                        );
                        setControlValue(
                            'hiddenCostPct',
                            this.defHiddenCostPct,
                            this.hiddenCostPctCtl,
                        );
                        setControlValue(
                            'expectedProfitPct',
                            this.defExpectedProfitPct,
                            this.expectedProfitPctCtl,
                        );
                        setControlValue(
                            'expectedProfitBaht',
                            this.defExpectedProfitBaht,
                            this.expectedProfitBahtCtl,
                        );
                        setControlValue(
                            'platformFeePct',
                            this.defPlatformFeePct,
                            this.platformFeePctCtl,
                        );
                        setControlValue(
                            'vatPct',
                            this.defVatPct,
                            this.vatPctCtl,
                        );

                        const useExpectedProfitPct: boolean = _get(
                            results,
                            'useExpectedProfitPct',
                            this.useExpectedProfitPct,
                        );
                        if (_isBoolean(useExpectedProfitPct)) {
                            this.useExpectedProfitPct = useExpectedProfitPct;
                        }
                        this.calculate();
                    }
                    this.endLoading('โหลดข้อมูลเสร็จ');
                });
        }
    }

    private saveParams(p: any): void {
        if (this.currentPid && !this.loading) {
            const saveProductSpecificParamsObservable: Observable<any> =
                this.priceCalSaveService.save({
                    pid: this.currentPid,
                    params: {
                        pYuanPrice: p['pYuanPrice'],
                        shippingChinaPct: p['shippingChinaPct'],
                        shippingThaiPct: p['shippingThaiPct'],
                        hiddenCostPct: p['hiddenCostPct'],
                        useExpectedProfitPct: p['useExpectedProfitPct'],
                        expectedProfitPct: p['expectedProfitPct'],
                        expectedProfitBaht: p['expectedProfitBaht'],
                    },
                });

            const saveGlobalParamsObservable: Observable<any> =
                this.priceCalSaveService.save({
                    pid: -1,
                    params: {
                        exchangeRate: p['exchangeRate'],
                        transactionFeePct: p['transactionFeePct'],
                        platformFeePct: p['platformFeePct'],
                        vatPct: p['vatPct'],
                    },
                });

            this.startLoading('กำลังบันทึก ...', false);
            forkJoin([
                saveProductSpecificParamsObservable,
                saveGlobalParamsObservable,
            ]).subscribe(
                (results) => {
                    if (!_every(results, (res) => res)) {
                        this.notificationService.pushMessage({
                            message: 'บันทึกข้อมูลไม่สำเร็จ',
                            messageType: MessageType.error,
                        });
                        this.endLoading('บันทึกข้อมูลไม่สำเร็จ');
                    } else {
                        this.endLoading();
                    }
                },
                (err) => {
                    this.notificationService.pushMessage({
                        message:
                            'บันทึกข้อมูลไม่สำเร็จ :' +
                            this.utilService.errorToString(err),
                        messageType: MessageType.error,
                    });
                    this.endLoading('บันทึกข้อมูลไม่สำเร็จ');
                },
            );
        }
    }

    private parseNumber(input: any): number | null {
        const cleanInput = _toString(input).trim();
        const num = _toNumber(cleanInput);
        if (_isFinite(num)) {
            return this.round(num);
        }
        return null;
    }

    private round(num: number): number {
        return _round(num, this.DECIMAL_POINT);
    }

    /**
     * Gross up `net` so that, after a `deductPct` cut taken as a percentage of
     * the resulting price, exactly `net` remains. Returns 0 when the deduction
     * is >= 100% (no finite price can cover the fees).
     */
    private grossUp(net: number, deductPct: number): number {
        const denominator = 1 - deductPct / 100;
        return denominator > 0 ? net / denominator : 0;
    }

    private validate(): CalParam | null {
        const calParam: CalParam = {
            pYuanPrice: this.parseNumber(this.pYuanPriceCtl.value),
            exchangeRate: this.parseNumber(this.exchangeRateCtl.value),
            shippingChinaPct: this.parseNumber(this.shippingChinaPctCtl.value),
            transactionFeePct: this.parseNumber(
                this.transactionFeePctCtl.value,
            ),
            shippingThaiPct: this.parseNumber(this.shippingThaiPctCtl.value),
            hiddenCostPct: this.parseNumber(this.hiddenCostPctCtl.value),
            expectedProfitPct: this.parseNumber(
                this.expectedProfitPctCtl.value,
            ),
            expectedProfitBaht: this.parseNumber(
                this.expectedProfitBahtCtl.value,
            ),
            platformFeePct: this.parseNumber(this.platformFeePctCtl.value),
            vatPct: this.parseNumber(
                (this.vatPctCtl as CalculatorInputComponent).value,
            ),
            useExpectedProfitPct: this.useExpectedProfitPct ? true : false,
        };

        for (const [, value] of Object.entries(calParam)) {
            if (_isNil(value)) {
                return null;
            }
        }
        return calParam;
    }

    private calculate(): CalParam | void {
        const calParam: CalParam | null = this.validate();
        if (!calParam) {
            return;
        }
        const validatedParams = calParam as Record<keyof CalParam, any>;

        // ราคาเฉพาะสินค้าจากจีน (บาท)
        this.calculatedProductCostFromChina = this.round(
            validatedParams.pYuanPrice * validatedParams.exchangeRate,
        );

        // The 1688 payment is the product plus the Chinese seller's domestic
        // shipping, and TrueMoney's cut is taken on that whole amount — so
        // this one cannot join the flat percentages below, which are each
        // taken on the product cost alone. Left unrounded: only the total is
        // rounded, as with every other displayed figure here, so the fee
        // doesn't pick up a rounding step of its own.
        const chinaPaymentAmount =
            this.calculatedProductCostFromChina *
            (1 + validatedParams.shippingChinaPct / 100);
        const transactionFee =
            (chinaPaymentAmount * validatedParams.transactionFeePct) / 100;
        this.calculatedChinaPaymentAmount = this.round(chinaPaymentAmount);
        this.calculatedTransactionFee = this.round(transactionFee);

        // ต้นทุนส่วนเพิ่ม (%)
        const _costPct =
            validatedParams.shippingChinaPct +
            validatedParams.shippingThaiPct +
            validatedParams.hiddenCostPct;

        this.calculatedTotalCost = this.round(
            this.calculatedProductCostFromChina * (1 + _costPct / 100) +
                transactionFee,
        );

        // คำนวนเปอร์เซ็นรวมที่ถูกหักตามช่องทาง
        //
        // VAT sits INSIDE the price the buyer pays, so what leaves the seller
        // is vat/(100+vat) of the gross — 6.54% at 7%, not 7%. Deducting the
        // rate itself removes ฿7.00 per ฿100 where the Revenue Department
        // takes ฿6.54, pricing every item ~0.5% high and disagreeing with
        // splitVatInclusive() in entity-config.ts, which is what the revenue
        // report actually books this same money at. Same family of error as
        // the create-tax-invoice trap that file warns about, mirrored.
        //
        // Falls out correctly before registration too: vatPct 0 => 0.
        const vatDeductPct =
            (validatedParams.vatPct / (100 + validatedParams.vatPct)) * 100;

        // The commission is a cut of the buyer-paid total, VAT included —
        // that is what the finance normalizers see on the statements, so it
        // is a straight percentage of the gross. Its own input VAT (7/107 of
        // the fee, claimable once registered) is deliberately NOT netted off:
        // pricing without it is the conservative side, so the reclaim shows
        // up as margin above plan instead of being given away in the price.
        const deductablePlatformPct =
            validatedParams.platformFeePct + vatDeductPct;
        const deductableLnwShopPct = vatDeductPct;

        this.calculatedTotalDeducted = this.round(deductablePlatformPct);

        // คำนวนกำไรที่คาดหวัง
        if (validatedParams.useExpectedProfitPct) {
            // ใช้เปอร์เซ็น
            validatedParams.expectedProfitBaht = this.round(
                (this.calculatedTotalCost * validatedParams.expectedProfitPct) /
                    100,
            );
            this.expectedProfitBahtCtl.value =
                validatedParams.expectedProfitBaht.toFixed(this.DECIMAL_POINT);
        } else {
            // หลีกเลี่ยงการหารด้วยศูนย์เมื่อต้นทุนรวมเป็น 0
            validatedParams.expectedProfitPct =
                this.calculatedTotalCost > 0
                    ? this.round(
                          (validatedParams.expectedProfitBaht /
                              this.calculatedTotalCost) *
                              100,
                      )
                    : 0;
            this.expectedProfitPctCtl.value =
                validatedParams.expectedProfitPct.toFixed(this.DECIMAL_POINT);
        }

        // คำนวนราคาขาย
        const basePrice =
            this.calculatedTotalCost + validatedParams.expectedProfitBaht;
        this.calculatedBahtSellingPlatformPrice = this.round(
            this.grossUp(basePrice, deductablePlatformPct),
        );

        this.calcuatedBahtSellingLnwShopPrice = this.round(
            this.grossUp(basePrice, deductableLnwShopPct),
        );
        this.markFieldsAsCalculated();
        this.cdref.detectChanges();
        return {
            pYuanPrice: validatedParams.pYuanPrice,
            shippingChinaPct: validatedParams.shippingChinaPct,
            transactionFeePct: validatedParams.transactionFeePct,
            shippingThaiPct: validatedParams.shippingThaiPct,
            hiddenCostPct: validatedParams.hiddenCostPct,
            useExpectedProfitPct: validatedParams.useExpectedProfitPct,
            expectedProfitPct: validatedParams.expectedProfitPct,
            expectedProfitBaht: validatedParams.expectedProfitBaht,
            exchangeRate: validatedParams.exchangeRate,
            platformFeePct: validatedParams.platformFeePct,
            vatPct: validatedParams.vatPct,
        };
    }

    /**
     * ราคาหลักที่หน้า "ตั้งราคา" จะเริ่มจาก = ราคาขาย LS ปัดขึ้นเป็นบาท — null ถ้ายังคำนวณไม่ได้
     * (หน้านั้นจะใช้ราคาหลักปัจจุบันของสินค้าแทน)
     */
    get suggestedBasePrice(): number | null {
        return this.calcuatedBahtSellingLnwShopPrice > 0
            ? Math.ceil(this.calcuatedBahtSellingLnwShopPrice - 1e-9)
            : null;
    }

    private markFieldsAsCalculated() {
        const fields: CalculatorInputComponent[] = [
            this.pYuanPriceCtl,
            this.exchangeRateCtl,
            this.shippingChinaPctCtl,
            this.transactionFeePctCtl,
            this.shippingThaiPctCtl,
            this.hiddenCostPctCtl,
            this.expectedProfitPctCtl,
            this.expectedProfitBahtCtl,
            this.platformFeePctCtl,
            this.vatPctCtl,
        ];
        for (const field of fields) {
            field.markAsCalculated();
        }
    }

    onUseExpectedProfitPctClick(event: MouseEvent): void {
        event.preventDefault();
        this.useExpectedProfitPct = true;
    }

    onUseExpectedProfitClick(event: MouseEvent): void {
        event.preventDefault();
        this.useExpectedProfitPct = false;
    }

    onEnter() {
        const calParams = this.calculate();
        // calculate() returns void when validation fails; don't try to save
        // an undefined params object (would throw on property access).
        if (calParams) {
            this.saveParams(calParams);
        }
    }
}

interface CalParam {
    pYuanPrice: number | null;
    exchangeRate: number | null;
    shippingChinaPct: number | null;
    transactionFeePct: number | null;
    shippingThaiPct: number | null;
    hiddenCostPct: number | null;
    platformFeePct: number | null;
    vatPct: number | null;
    expectedProfitPct: number | null;
    expectedProfitBaht: number | null;
    useExpectedProfitPct: boolean | null;
}
